import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Grid, TextField, Button, Snackbar, Alert, Divider, List, ListItem, ListItemText, IconButton, Collapse } from '@mui/material';
import { Add, Remove, ExpandLess, ExpandMore } from '@mui/icons-material';
import { getDocument, findDocumentsByField, addDocument, updateDocument } from '../services/firestoreService';
import { useAuth } from '../context/AuthContext';

const BASIC_SUPPLIES = [
  { key: 'kitCirugia', label: 'Kit Cirugía' },
  { key: 'kitInsumos', label: 'Kit Insumos' },
  { key: 'compresas', label: 'Compresas' },
  { key: 'apositos', label: 'Apósitos' },
  { key: 'bataTela', label: 'Bata Tela' },
  { key: 'bataDesechable', label: 'Bata Desechable' },
  { key: 'panoCampo', label: 'Paño de Campo' },
];

const ABSORBABLE_TYPES = ['Monosyn', 'Polyglicolico'];
const ABSORBABLE_NUMBERS = ['0', '1', '2/0', '3/0', '4/0'];

const NON_ABS_TYPES = ['Nylon', 'Polypropileno', 'Seda'];
const NON_ABS_NUMBERS = ['2/0', '3/0'];

const GLOVE_SIZES = ['6', '6.5', '7', '7.5', '8', '8.5'];
const TRAQUEOTUBE_SIZES = ['3', '3.5', '4', '4.5', '5', '5.5', '6', '6.5', '7', '7.5', '8', '8.5', '9'];

const createInitialAbsorbable = () => {
  const initial = {};
  ABSORBABLE_TYPES.forEach((type) => {
    initial[type] = {};
    ABSORBABLE_NUMBERS.forEach((num) => {
      initial[type][num] = 0;
    });
  });
  return initial;
};

const createInitialNonAbsorbable = () => {
  const initial = {};
  NON_ABS_TYPES.forEach((type) => {
    initial[type] = {};
    NON_ABS_NUMBERS.forEach((num) => {
      initial[type][num] = 0;
    });
  });
  return initial;
};

const createInitialGloves = () => {
  const initial = {};
  GLOVE_SIZES.forEach((size) => {
    initial[size] = 0;
  });
  return initial;
};

const createInitialTraqueotubes = () => {
  const initial = {};
  TRAQUEOTUBE_SIZES.forEach((size) => {
    initial[size] = 0;
  });
  return initial;
};

const QuantitySelector = ({ value, onChange, min = 0, max }) => {
  const handleChange = (delta) => {
    const current = Number.isFinite(value) ? value : 0;
    let next = current + delta;
    if (typeof min === 'number' && next < min) next = min;
    if (typeof max === 'number' && next > max) next = max;
    onChange(next);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderRadius: 999,
        border: '1px solid',
        borderColor: 'divider',
        px: 0.75,
        py: 0.25,
        minWidth: 80,
        justifyContent: 'space-between',
      }}
    >
      <IconButton size="small" onClick={() => handleChange(-1)}>
        <Remove fontSize="small" />
      </IconButton>
      <Typography sx={{ minWidth: 24, textAlign: 'center' }}>
        {value ?? 0}
      </Typography>
      <IconButton size="small" onClick={() => handleChange(1)}>
        <Add fontSize="small" />
      </IconButton>
    </Box>
  );
};

const SurgerySupplies = () => {
  const { surgeryId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { userData } = useAuth();

  const [surgery, setSurgery] = useState(null);
  const [suppliesDoc, setSuppliesDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [basicSupplies, setBasicSupplies] = useState({
    kitCirugia: 0,
    kitInsumos: 0,
    compresas: 0,
    apositos: 0,
    bataTela: 0,
    bataDesechable: 0,
    panoCampo: 0,
  });

  const [absorbableSutures, setAbsorbableSutures] = useState(() => createInitialAbsorbable());
  const [nonAbsorbableSutures, setNonAbsorbableSutures] = useState(() => createInitialNonAbsorbable());
  const [sterileGloves, setSterileGloves] = useState(() => createInitialGloves());
  const [traqueotubes, setTraqueotubes] = useState(() => createInitialTraqueotubes());
  const [instruments, setInstruments] = useState({
    cajaCirugia: 0,
    rinon: 0,
  });

  const [openAbsorbable, setOpenAbsorbable] = useState(false);
  const [openNonAbsorbable, setOpenNonAbsorbable] = useState(false);
  const [openAbsTypes, setOpenAbsTypes] = useState(() => {
    const initial = {};
    ABSORBABLE_TYPES.forEach((type) => {
      initial[type] = false;
    });
    return initial;
  });
  const [openNonAbsTypes, setOpenNonAbsTypes] = useState(() => {
    const initial = {};
    NON_ABS_TYPES.forEach((type) => {
      initial[type] = false;
    });
    return initial;
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        let baseSurgery = location.state?.surgery || null;
        if (!baseSurgery && surgeryId) baseSurgery = await getDocument('surgeries', surgeryId);
        if (!baseSurgery) {
          setSnackbar({ open: true, message: 'No se encontró la cirugía', severity: 'error' });
          setLoading(false);
          return;
        }
        setSurgery(baseSurgery);

        const existing = await findDocumentsByField('surgerySupplies', 'surgeryId', baseSurgery.id || surgeryId);
        if (existing.length > 0) {
          const doc = existing[0];
          setSuppliesDoc(doc);

          if (doc.basicSupplies) {
            setBasicSupplies((prev) => ({ ...prev, ...doc.basicSupplies }));
          }
          if (doc.absorbableSutures) {
            setAbsorbableSutures((prev) => {
              const merged = createInitialAbsorbable();
              ABSORBABLE_TYPES.forEach((type) => {
                ABSORBABLE_NUMBERS.forEach((num) => {
                  const current = doc.absorbableSutures?.[type]?.[num];
                  merged[type][num] = typeof current === 'number' ? current : prev[type]?.[num] || 0;
                });
              });
              return merged;
            });
          }
          if (doc.nonAbsorbableSutures) {
            setNonAbsorbableSutures((prev) => {
              const merged = createInitialNonAbsorbable();
              NON_ABS_TYPES.forEach((type) => {
                NON_ABS_NUMBERS.forEach((num) => {
                  const current = doc.nonAbsorbableSutures?.[type]?.[num];
                  merged[type][num] = typeof current === 'number' ? current : prev[type]?.[num] || 0;
                });
              });
              return merged;
            });
          }
          if (doc.sterileGloves) {
            setSterileGloves((prev) => {
              const merged = createInitialGloves();
              GLOVE_SIZES.forEach((size) => {
                const current = doc.sterileGloves?.[size];
                merged[size] = typeof current === 'number' ? current : prev[size] || 0;
              });
              return merged;
            });
          }
          if (doc.traqueotubes) {
            setTraqueotubes((prev) => {
              const merged = createInitialTraqueotubes();
              TRAQUEOTUBE_SIZES.forEach((size) => {
                const current = doc.traqueotubes?.[size];
                merged[size] = typeof current === 'number' ? current : prev[size] || 0;
              });
              return merged;
            });
          }
          if (doc.instruments) {
            setInstruments((prev) => ({ ...prev, ...doc.instruments }));
          }
        }
      } catch (e) {
        console.error(e);
        setSnackbar({ open: true, message: 'Error al cargar insumos', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surgeryId, location.state]);

  const parseQuantity = (value) => {
    const n = Number(value);
    if (Number.isNaN(n) || n < 0) return 0;
    return n;
  };

  const handleBasicSupplyChange = (key, value) => {
    setBasicSupplies((prev) => ({
      ...prev,
      [key]: parseQuantity(value),
    }));
  };

  const handleAbsorbableChange = (type, num, value) => {
    setAbsorbableSutures((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [num]: parseQuantity(value),
      },
    }));
  };

  const handleNonAbsorbableChange = (type, num, value) => {
    setNonAbsorbableSutures((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [num]: parseQuantity(value),
      },
    }));
  };

  const handleGloveChange = (size, value) => {
    setSterileGloves((prev) => ({
      ...prev,
      [size]: parseQuantity(value),
    }));
  };

  const handleTraqueotubeChange = (size, value) => {
    setTraqueotubes((prev) => ({
      ...prev,
      [size]: parseQuantity(value),
    }));
  };

  const handleInstrumentChange = (key, value) => {
    setInstruments((prev) => ({
      ...prev,
      [key]: parseQuantity(value),
    }));
  };

  const handleSave = async () => {
    if (!surgery) return;
    setSaving(true);
    try {
      const now = new Date();
      const data = {
        surgeryId: surgery.id || surgeryId,
        patientId: surgery.patientId || null,
        patientName: surgery.patientName || '',
        patientSpecies: surgery.patientSpecies || '',
        patientBreed: surgery.patientBreed || '',
        patientWeight: surgery.patientWeight || '',
        procedureType: surgery.procedureType || '',
        date: surgery.date || '',
        startTime: surgery.startTime || '',
        endTime: surgery.endTime || '',
        mainSurgeon: surgery.mainSurgeon || '',
        anesthesiologist: surgery.anesthesiologist || '',
        basicSupplies,
        absorbableSutures,
        nonAbsorbableSutures,
        sterileGloves,
        traqueotubes,
        instruments,
        updatedAt: now,
        createdAt: suppliesDoc?.createdAt || now,
        createdBy: suppliesDoc?.createdBy || userData?.name || userData?.email || 'Usuario',
      };

      if (suppliesDoc) {
        await updateDocument('surgerySupplies', suppliesDoc.id, data);
      } else {
        await addDocument('surgerySupplies', data);
      }

      setSnackbar({ open: true, message: 'Insumos guardados correctamente', severity: 'success' });
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Error al guardar insumos', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Cargando insumos de la cirugía...</Typography>
      </Box>
    );
  }

  if (!surgery) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">No se encontró la cirugía para cargar insumos.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Cargar Insumos</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/surgery-scheduler')}>
            Volver a Programación
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar Insumos'}
          </Button>
        </Box>
      </Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Datos del Paciente y Cirugía</Typography>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <TextField
              label="Paciente"
              value={surgery.patientName || ''}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <TextField
              label="Especie/Raza"
              value={`${surgery.patientSpecies || ''} ${surgery.patientBreed || ''}`.trim()}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 2
            }}>
            <TextField
              label="Peso (kg)"
              value={surgery.patientWeight || ''}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 2
            }}>
            <TextField
              label="Fecha"
              value={surgery.date || ''}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <TextField
              label="Procedimiento"
              value={surgery.procedureType || ''}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <TextField
              label="Cirujano Principal"
              value={surgery.mainSurgeon || ''}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <TextField
              label="Anestesiólogo"
              value={surgery.anesthesiologist || ''}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>
      </Paper>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Insumos</Typography>
        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle1" gutterBottom>Insumos básicos</Typography>
        <List dense>
          {BASIC_SUPPLIES.map((item) => (
            <ListItem key={item.key} sx={{ px: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  justifyContent: 'flex-start',
                  gap: 2,
                }}
              >
                <ListItemText primary={item.label} sx={{ flex: '0 0 200px', mr: 1 }} />
                <QuantitySelector
                  value={basicSupplies[item.key] ?? 0}
                  onChange={(qty) => handleBasicSupplyChange(item.key, qty)}
                />
              </Box>
            </ListItem>
          ))}
        </List>

        <Typography variant="subtitle1" sx={{ mt: 3 }} gutterBottom>
          Suturas
        </Typography>
        <List dense>
          {/* Sutura Absorbible */}
          <ListItem
            button
            sx={{ px: 0 }}
            onClick={() => setOpenAbsorbable((prev) => !prev)}
          >
            <ListItemText primary="Sutura Absorbible" />
            {openAbsorbable ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse in={openAbsorbable} timeout="auto" unmountOnExit>
            <List component="div" disablePadding dense>
              {ABSORBABLE_TYPES.map((type) => (
                <React.Fragment key={type}>
                  <ListItem
                    button
                    sx={{ pl: 2 }}
                    onClick={() =>
                      setOpenAbsTypes((prev) => ({ ...prev, [type]: !prev[type] }))
                    }
                  >
                    <ListItemText primary={type} />
                    {openAbsTypes[type] ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Collapse in={openAbsTypes[type]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding dense>
                      {ABSORBABLE_NUMBERS.map((num) => (
                        <ListItem key={`${type}-${num}`} sx={{ pl: 4 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                              justifyContent: 'flex-start',
                              gap: 2,
                            }}
                          >
                            <ListItemText primary={`N° ${num}`} sx={{ flex: '0 0 80px', mr: 1 }} />
                            <QuantitySelector
                              value={absorbableSutures[type]?.[num] ?? 0}
                              onChange={(qty) => handleAbsorbableChange(type, num, qty)}
                            />
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              ))}
            </List>
          </Collapse>

          {/* Sutura No Absorbible */}
          <ListItem
            button
            sx={{ px: 0, mt: 1 }}
            onClick={() => setOpenNonAbsorbable((prev) => !prev)}
          >
            <ListItemText primary="Sutura No Absorbible" />
            {openNonAbsorbable ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse in={openNonAbsorbable} timeout="auto" unmountOnExit>
            <List component="div" disablePadding dense>
              {NON_ABS_TYPES.map((type) => (
                <React.Fragment key={type}>
                  <ListItem
                    button
                    sx={{ pl: 2 }}
                    onClick={() =>
                      setOpenNonAbsTypes((prev) => ({ ...prev, [type]: !prev[type] }))
                    }
                  >
                    <ListItemText primary={type} />
                    {openNonAbsTypes[type] ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Collapse in={openNonAbsTypes[type]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding dense>
                      {NON_ABS_NUMBERS.map((num) => (
                        <ListItem key={`${type}-${num}`} sx={{ pl: 4 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                              justifyContent: 'flex-start',
                              gap: 2,
                            }}
                          >
                            <ListItemText primary={`N° ${num}`} sx={{ flex: '0 0 auto', mr: 1 }} />
                            <QuantitySelector
                              value={nonAbsorbableSutures[type]?.[num] ?? 0}
                              onChange={(qty) => handleNonAbsorbableChange(type, num, qty)}
                            />
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              ))}
            </List>
          </Collapse>
        </List>

        <Typography variant="subtitle1" sx={{ mt: 3 }} gutterBottom>
          Guante Estéril
        </Typography>
        <List dense>
          {GLOVE_SIZES.map((size) => (
            <ListItem key={size} sx={{ px: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  justifyContent: 'flex-start',
                  gap: 2,
                }}
              >
                <ListItemText primary={`Guante Estéril N° ${size}`} sx={{ flex: '0 0 200px', mr: 1 }} />
                <QuantitySelector
                  value={sterileGloves[size] ?? 0}
                  onChange={(qty) => handleGloveChange(size, qty)}
                />
              </Box>
            </ListItem>
          ))}
        </List>

        <Typography variant="subtitle1" sx={{ mt: 3 }} gutterBottom>
          Traqueotubo
        </Typography>
        <List dense>
          {TRAQUEOTUBE_SIZES.map((size) => (
            <ListItem key={size} sx={{ px: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  justifyContent: 'flex-start',
                  gap: 2,
                }}
              >
                <ListItemText primary={`Traqueotubo N° ${size}`} sx={{ flex: '0 0 200px', mr: 1 }} />
                <QuantitySelector
                  value={traqueotubes[size] ?? 0}
                  onChange={(qty) => handleTraqueotubeChange(size, qty)}
                />
              </Box>
            </ListItem>
          ))}
        </List>
      </Paper>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Instrumental</Typography>
        <Divider sx={{ mb: 2 }} />
        <List dense>
          <ListItem sx={{ px: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'flex-start',
                gap: 2,
              }}
            >
              <ListItemText primary="Caja Cirugía" sx={{ flex: '0 0 200px', mr: 1 }} />
              <QuantitySelector
                value={instruments.cajaCirugia ?? 0}
                onChange={(qty) => handleInstrumentChange('cajaCirugia', qty)}
              />
            </Box>
          </ListItem>
          <ListItem sx={{ px: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'flex-start',
                gap: 2,
              }}
            >
              <ListItemText primary="Riñón" sx={{ flex: '0 0 200px', mr: 1 }} />
              <QuantitySelector
                value={instruments.rinon ?? 0}
                onChange={(qty) => handleInstrumentChange('rinon', qty)}
              />
            </Box>
          </ListItem>
        </List>
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SurgerySupplies;
